import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import type { CalendarEvent } from '../mock-data'

interface ICalendarEventCardProps {
  event: CalendarEvent
  /**
   * Events overlapping this one that no card was drawn for. A row splits into two
   * lanes at most, so the card in the last lane carries the count — otherwise a
   * third concurrent event would simply vanish from the day.
   */
  overflowCount?: number
}

const ACCENT_TINT = '22'

const OverflowBadge = ({ count }: { count: number }) => (
  <Box px="8px" h="20px" borderRadius="6px" bg="rgba(255,255,255,0.10)" display="inline-flex" alignItems="center">
    <Text fontSize="10px" fontWeight={700} color="rgba(255,255,255,0.80)" m={0} whiteSpace="nowrap">
      +{count}
    </Text>
  </Box>
)

const TeamBadge = ({ label, accent }: { label: string; accent: string }) => (
  <Box
    px="8px"
    h="20px"
    borderRadius="6px"
    bg={`${accent}${ACCENT_TINT}`}
    display="inline-flex"
    alignItems="center"
    minW={0}
  >
    <Text fontSize="9px" fontWeight={700} color={accent} m={0} letterSpacing="0.06em" noOfLines={1}>
      {label.toUpperCase()}
    </Text>
  </Box>
)

const CalendarEventCard = ({ event, overflowCount = 0 }: ICalendarEventCardProps) => {
  const accent = event.teamTag.color

  return (
    <Box
      bg="#101a2d"
      border="1px solid rgba(194, 199, 214, 0.08)"
      borderLeft="3px solid"
      borderLeftColor={accent}
      borderRadius="12px"
      px="12px"
      py="10px"
      h="100%"
      overflow="hidden"
    >
      <HStack justify="space-between" align="flex-start" spacing="8px" w="100%">
        <VStack spacing="2px" align="flex-start" flex={1} minW={0}>
          <Text color="white" fontSize="13px" fontWeight={600} m={0} lineHeight="1.2" noOfLines={1}>
            {event.title}
          </Text>
          <Text color="rgba(255,255,255,0.65)" fontSize="11px" m={0} noOfLines={1}>
            🕐 {event.start} – {event.end}
          </Text>
          {event.location && (
            <Text color="rgba(255,255,255,0.55)" fontSize="11px" m={0} noOfLines={1}>
              📍 {event.location}
            </Text>
          )}
        </VStack>
        <VStack spacing="4px" align="flex-end" flexShrink={0} minW={0} maxW="45%">
          <TeamBadge label={event.teamTag.label} accent={accent} />
          {overflowCount > 0 && <OverflowBadge count={overflowCount} />}
        </VStack>
      </HStack>
    </Box>
  )
}

export default CalendarEventCard
